import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Link,
  Tooltip,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarTodayIcon,
  OpenInNew as OpenInNewIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatIncidentDate } from '../utils/caseStudyDates.js';
import { REPS_COLORS, oeBand } from '../utils/constants';
import { Highlight, SnippetList } from './Highlight.jsx';

/** Map source values to display labels */
const SOURCE_LABELS = {
  csb: 'CSB Report',
  internal: 'Internal',
  audit: 'Audit',
  observation: 'Observation',
  regulatory: 'Regulatory',
  other: 'Other',
};

/** Map source values to chip colors */
const SOURCE_COLORS = {
  csb: '#1565c0',
  internal: '#00897b',
  audit: '#ef6c00',
  observation: '#7b1fa2',
  regulatory: '#c62828',
  other: '#757575',
};

/** CSB branding colors and styles */
const CSB_BRAND = {
  primary: '#003366', // CSB navy blue
  secondary: '#1565c0',
  accent: '#e3f2fd', // Light blue tint
  borderColor: '#003366',
  badgeLabel: 'CSB Investigation',
};

/** REPS quadrant labels */
const REPS_LABELS = {
  reliability: 'R',
  efficiency: 'E',
  productivity: 'P',
  safety: 'S',
};

/**
 * Get chip background color based on score severity (0-10).
 * Higher scores indicate more relevance/concern.
 */
function getScoreChipColor(score) {
  // Higher score = more entropy/concern → green (low) through yellow/orange to red (high).
  return oeBand(score).color;
}

/**
 * Format a date value for display.
 * Handles Firestore Timestamps, ISO strings, and Date objects.
 */
function formatDate(dateValue) {
  if (!dateValue) return null;
  let date;
  if (dateValue.toDate) {
    date = dateValue.toDate();
  } else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    return null;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * CaseStudyCard - Displays a case study summary as an MUI Card.
 *
 * @param {Object} props
 * @param {Object} props.caseStudy - The case study data object
 * @param {boolean} [props.compact=false] - If true, renders a compact version for dashboard use
 * @param {string}  [props.searchQuery] - active library search (P5-08): hits are highlighted and carried to the detail page via ?q=
 * @param {Object}  [props.hit] - search result ({ where, snippets }) for this card
 */
export default function CaseStudyCard({ caseStudy, compact = false, searchQuery = '', hit = null }) {
  const navigate = useNavigate();

  if (!caseStudy) return null;

  const {
    id,
    title,
    summary,
    source,
    industry,
    repsMapping,
    incidentDate,
    createdAt,
    csbUrl,
  } = caseStudy;

  const isCSB = source === 'csb';

  const sourceLabel = SOURCE_LABELS[source] || source || 'Unknown';
  const sourceColor = SOURCE_COLORS[source] || '#757575';
  // P5-16: the 2024-01-01 extraction placeholder is not a real incident date
  const displayDate = formatIncidentDate(incidentDate, { year: 'numeric', month: 'short', day: 'numeric' }) || formatDate(createdAt);

  const handleViewDetails = () => {
    const q = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '';
    navigate(`/drew/case-studies/${id}${q}`);
  };

  // Compact mode for dashboard widgets
  if (compact) {
    return (
      <Card
        sx={{
          cursor: 'pointer',
          transition: 'box-shadow 0.2s, transform 0.15s',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-1px)',
          },
          ...(isCSB && {
            borderLeft: `3px solid ${CSB_BRAND.borderColor}`,
            bgcolor: CSB_BRAND.accent,
          }),
        }}
        onClick={handleViewDetails}
      >
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {isCSB ? (
              <Tooltip title="U.S. Chemical Safety and Hazard Investigation Board">
                <Chip
                  icon={<VerifiedUserIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                  label={CSB_BRAND.badgeLabel}
                  size="small"
                  sx={{
                    bgcolor: CSB_BRAND.primary,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 22,
                    '& .MuiChip-icon': { ml: '4px' },
                  }}
                />
              </Tooltip>
            ) : (
              <Chip
                label={sourceLabel}
                size="small"
                sx={{
                  bgcolor: sourceColor,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
            {industry && (
              <Chip
                label={industry.replace(/_/g, ' ')}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
              />
            )}
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          {repsMapping && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {Object.entries(REPS_LABELS).map(([key, label]) => {
                const score = repsMapping[key];
                if (score == null) return null;
                return (
                  <Chip
                    key={key}
                    label={`${label}: ${score}`}
                    size="small"
                    sx={{
                      bgcolor: `${REPS_COLORS[key]}18`,
                      color: REPS_COLORS[key],
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 20,
                      border: `1px solid ${REPS_COLORS[key]}40`,
                    }}
                  />
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full mode for case study library
  return (
    <Card
      sx={{
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        ...(isCSB && {
          borderLeft: `4px solid ${CSB_BRAND.borderColor}`,
          bgcolor: CSB_BRAND.accent,
        }),
      }}
    >
      <CardContent>
        {/* Header: source badge, industry tag, date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          {isCSB ? (
            <Tooltip title="U.S. Chemical Safety and Hazard Investigation Board">
              <Chip
                icon={<VerifiedUserIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
                label={CSB_BRAND.badgeLabel}
                size="small"
                sx={{
                  bgcolor: CSB_BRAND.primary,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 26,
                  '& .MuiChip-icon': { ml: '4px' },
                }}
              />
            </Tooltip>
          ) : (
            <Chip
              label={sourceLabel}
              size="small"
              sx={{
                bgcolor: sourceColor,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}
          {industry && (
            <Chip
              label={industry.replace(/_/g, ' ')}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}
            />
          )}
          {displayDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {displayDate}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Title */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
          <Highlight text={title} query={searchQuery} />
        </Typography>

        {/* Summary */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.6,
          }}
        >
          <Highlight text={summary} query={searchQuery} />
        </Typography>

        {/* P5-08: where the search phrase was found (document text is not otherwise shown on the card) */}
        {hit?.where === 'document' && (
          <Box sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Found in the source document
            </Typography>
            <SnippetList snippets={hit.snippets} />
          </Box>
        )}

        {/* REPS Chips */}
        {repsMapping && (
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
            {Object.entries(REPS_LABELS).map(([key, label]) => {
              const score = repsMapping[key];
              if (score == null) return null;
              const chipColor = getScoreChipColor(score);
              return (
                <Chip
                  key={key}
                  label={`${label}: ${score}/10`}
                  size="small"
                  sx={{
                    bgcolor: `${REPS_COLORS[key]}15`,
                    color: REPS_COLORS[key],
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: `1px solid ${REPS_COLORS[key]}40`,
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
          {isCSB && csbUrl && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              component={Link}
              href={csbUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: CSB_BRAND.primary,
                borderColor: CSB_BRAND.primary,
                '&:hover': {
                  borderColor: CSB_BRAND.secondary,
                  bgcolor: `${CSB_BRAND.primary}08`,
                },
              }}
            >
              View Original Report
            </Button>
          )}
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={handleViewDetails}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
